/**
 * SAID-DIIS — Student Portal shell.
 *
 * Rendered from App.js only when `user.role === 'student'`. Provides its own
 * top bar, navigation and page routing (internal state, no react-router use
 * to avoid touching the rest of the app). Consumes the same axios auth
 * header wired up by /api/client.js.
 *
 * Every data fetch here hits an existing, unmodified backend endpoint.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Home as HomeIcon, Package, Layers, ClipboardList, RotateCcw, LogOut, KeyRound, UserCog, ChevronDown } from 'lucide-react';
import Avatar from '../components/Avatar';
import './styles.css';
import { apiClient } from '../api/client';
import { endpoints } from '../api/endpoints';
import { Toast } from './ui';
import HomePage from './pages/Home';
import MyKitPage from './pages/MyKit';
import LibraryPage from './pages/Library';
import RequestsPage from './pages/Requests';
import ReturnsPage from './pages/Returns';
import NewRequestPage from './pages/NewRequest';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_implant-inventory/artifacts/g4gfj0jd_SAID%20logo.png';

const NAV_ITEMS = [
  { id: 'home',       label: 'Home',         icon: HomeIcon },
  { id: 'kit',        label: 'My Kit',       icon: Package },
  { id: 'library',    label: 'Components',   icon: Layers },
  { id: 'requests',   label: 'My Requests',  icon: ClipboardList },
  { id: 'returns',    label: 'Returns',      icon: RotateCcw },
];

const MOBILE_TABS = ['home', 'library', 'kit', 'requests'];

export default function StudentPortal({ user, onChangePassword, onEditProfile, onLogout }) {
  const [page, setPage] = useState('home');
  const [newRequestOpen, setNewRequestOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const menuRef = useRef(null);

  // Global data slices. Loaded once at portal mount, then refreshed on demand.
  const [components, setComponents] = useState([]);
  const [brands, setBrands]         = useState([]);
  const [orders, setOrders]         = useState([]);
  const [waitlist, setWaitlist]     = useState([]);
  const [returns, setReturns]       = useState([]);
  const [returnables, setReturnables] = useState([]);
  const [faculty, setFaculty]       = useState([]);
  const [loading, setLoading]       = useState(true);

  // ── Toast helper ─────────────────────────────────────────
  const showToast = useCallback((message, variant = 'ok') => {
    setToast({ message, variant });
    window.setTimeout(() => setToast(null), 4200);
  }, []);

  // ── Data loaders (each is a thin wrapper over apiClient) ─
  const loadComponents = useCallback(async () => {
    try {
      const { data } = await apiClient.get(endpoints.components.list);
      setComponents(Array.isArray(data) ? data : []);
    } catch (e) { console.error('components', e); }
  }, []);
  const loadBrands = useCallback(async () => {
    try {
      const { data } = await apiClient.get(endpoints.brands.list);
      setBrands(Array.isArray(data) ? data : []);
    } catch (e) { console.error('brands', e); }
  }, []);
  const loadOrders = useCallback(async () => {
    try {
      const { data } = await apiClient.get(endpoints.orders.list);
      setOrders(Array.isArray(data) ? data : []);
    } catch (e) { console.error('orders', e); }
  }, []);
  const loadWaitlist = useCallback(async () => {
    try {
      const { data } = await apiClient.get(endpoints.waitlist.list);
      setWaitlist(Array.isArray(data) ? data : []);
    } catch (e) { console.error('waitlist', e); }
  }, []);
  const loadReturns = useCallback(async () => {
    try {
      const { data } = await apiClient.get(endpoints.returns.list);
      setReturns(Array.isArray(data) ? data : []);
    } catch (e) { console.error('returns', e); }
  }, []);
  const loadReturnables = useCallback(async () => {
    try {
      const { data } = await apiClient.get(endpoints.returns.myReturnables);
      setReturnables(data?.items || []);
    } catch (e) { console.error('returnables', e); }
  }, []);
  const loadFaculty = useCallback(async () => {
    try {
      const { data } = await apiClient.get(endpoints.orders.facultyList);
      setFaculty(data?.faculty || []);
    } catch (e) { console.error('faculty', e); }
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      loadComponents(), loadBrands(), loadOrders(),
      loadWaitlist(), loadReturns(), loadReturnables(), loadFaculty(),
    ]);
    setLoading(false);
  }, [loadComponents, loadBrands, loadOrders, loadWaitlist, loadReturns, loadReturnables, loadFaculty]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  // Close user menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  // ── Derived counts for stat blocks / nav badges ──────────
  const counts = useMemo(() => {
    const kitCount = returnables.filter(r =>
      r.return_status === 'returnable' || r.return_status === 'partially_returned'
    ).reduce((s, r) => s + (r.qty_outstanding || 0), 0);

    const pendingRequests = orders.filter(o =>
      ['pending', 'approved'].includes((o.status || '').toLowerCase())
    ).length;

    const toReturn = returnables.filter(r =>
      r.return_status === 'returnable' || r.return_status === 'partially_returned'
    ).length;

    return { kitCount, pendingRequests, toReturn };
  }, [returnables, orders]);

  const activeMobileTab = MOBILE_TABS.includes(page) ? page : 'home';

  const initials = (user?.full_name || user?.username || '·')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(s => s[0].toUpperCase())
    .join('');

  // Bundle data + refresher into a "portal ctx" that every page receives.
  const ctx = {
    user,
    loading,
    components,
    brands,
    orders,
    waitlist,
    returns,
    returnables,
    faculty,
    counts,
    showToast,
    openNewRequest: (prefill) => {
      setNewRequestPrefill(prefill || null);
      setNewRequestOpen(true);
    },
    goto: setPage,
    refresh: {
      all: refreshAll,
      components: loadComponents,
      orders: loadOrders,
      returns: loadReturns,
      returnables: loadReturnables,
      waitlist: loadWaitlist,
    },
  };

  const [newRequestPrefill, setNewRequestPrefill] = useState(null);

  return (
    <div className="student-portal" data-testid="student-portal">
      {/* ── TOP BAR ────────────────────────────────────────── */}
      <header className="sp-topbar">
        <div className="sp-topbar-brand">
          <img className="sp-topbar-logo" src={LOGO_URL} alt="SAID" />
          <div className="sp-topbar-wordmark">
            <strong>SAID <span style={{ fontFamily: 'var(--diis-font-sans)', fontWeight: 500, fontSize: 10, letterSpacing: '0.24em', color: 'var(--diis-ink-subtle)', marginLeft: 4, verticalAlign: 2 }}>— DIIS</span></strong>
            <span>Student Portal</span>
          </div>
        </div>

        <div className="sp-topbar-user" ref={menuRef} style={{ position: 'relative' }}>
          <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column' }} className="sp-topbar-user-block">
            <span className="sp-topbar-user-name">{user?.full_name || user?.username}</span>
            <span className="sp-topbar-user-role">Student · {user?.student_category || '—'}</span>
          </div>
          <button
            type="button"
            className="sp-topbar-menu-btn"
            onClick={() => setMenuOpen(v => !v)}
            data-testid="portal-user-menu-btn"
            aria-label="Account menu"
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <span className="sp-avatar" aria-hidden="true" style={{ padding: 0, overflow: 'hidden' }}>
              <Avatar user={user} size={28} testId="sp-header-avatar" />
            </span>
            <ChevronDown size={14} />
          </button>

          {menuOpen && (
            <div className="sp-user-menu" role="menu" data-testid="portal-user-menu">
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onEditProfile && onEditProfile(); }}
                data-testid="portal-edit-profile-btn"
              ><UserCog size={15} /> Edit profile</button>
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onChangePassword && onChangePassword(); }}
                data-testid="portal-change-password-btn"
              ><KeyRound size={15} /> Change password</button>
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onLogout && onLogout(); }}
                data-testid="portal-logout-btn"
              ><LogOut size={15} /> Sign out</button>
            </div>
          )}
        </div>
      </header>

      {/* ── LAYOUT ─────────────────────────────────────────── */}
      <div className="sp-layout">
        {/* Desktop side nav */}
        <nav className="sp-sidenav" aria-label="Portal navigation">
          <div className="sp-nav-group-label">MY WORKSPACE</div>
          {NAV_ITEMS.map(({ id, label, icon: Icon }) => {
            const active = page === id;
            let count;
            if (id === 'kit') count = counts.kitCount || undefined;
            if (id === 'requests') count = counts.pendingRequests || undefined;
            if (id === 'returns') count = counts.toReturn || undefined;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setPage(id)}
                className={`sp-nav-item${active ? ' is-active' : ''}`}
                data-testid={`portal-nav-${id}`}
              >
                <Icon size={17} strokeWidth={1.7} />
                <span>{label}</span>
                {count ? <span className="sp-nav-count">{count}</span> : null}
              </button>
            );
          })}
        </nav>

        {/* Main content */}
        <main className="sp-main">
          <div className="sp-page">
            {page === 'home' && <HomePage ctx={ctx} />}
            {page === 'kit' && <MyKitPage ctx={ctx} />}
            {page === 'library' && <LibraryPage ctx={ctx} />}
            {page === 'requests' && <RequestsPage ctx={ctx} />}
            {page === 'returns' && <ReturnsPage ctx={ctx} />}
          </div>
        </main>
      </div>

      {/* Mobile bottom tab bar */}
      <nav className="sp-tabbar" aria-label="Mobile navigation">
        {[
          { id: 'home', label: 'Home', icon: HomeIcon },
          { id: 'library', label: 'Search', icon: Layers },
          { id: 'kit', label: 'My Kit', icon: Package },
          { id: 'requests', label: 'Requests', icon: ClipboardList },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setPage(id)}
            className={`sp-tab${activeMobileTab === id ? ' is-active' : ''}`}
            data-testid={`portal-tab-${id}`}
          >
            <Icon size={20} strokeWidth={1.7} />
            <span>{label}</span>
          </button>
        ))}
      </nav>

      {/* New Request modal */}
      <NewRequestPage
        open={newRequestOpen}
        onClose={() => { setNewRequestOpen(false); setNewRequestPrefill(null); }}
        ctx={ctx}
        prefill={newRequestPrefill}
      />

      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
